import React, { useState } from 'react';
import { Card, Button, Modal, Form } from 'react-bootstrap';
import { FileText, InfoCircle, BoxArrowUp, Clipboard, ClipboardCheck, ChevronRight, Files } from 'react-bootstrap-icons';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import useCollections, { CollectionModal } from '../../hooks/useCollections';
import openCapacitorSite from '../../utils/browserUtils';
import './FeedItem.css';

// Titles are only truncated to stop pathological outliers (some federal
// document titles run many hundreds of characters). Normal titles should
// render in full.
const TITLE_MAX_LENGTH = 300;

// Crawl items are identified by their page URL rather than a title, so the URL
// is shown under the title. Federal paths run long, so it gets trimmed for
// display — the full value stays on the element's title attribute and on the
// Source button. The CSS clamps to one line as a backstop.
const URL_MAX_LENGTH = 70;

// Latest-action text for congressional bills is unbounded - multi-committee
// referrals list every committee and run past 300 characters. Left alone the
// row grows tall enough to push the footer buttons out of the card, which has
// overflow hidden. Truncating here (rather than clamping in CSS) ends the text
// at a word boundary with an ellipsis instead of slicing a line in half.
// Sized per variant: the default card has 12px text and more vertical room
// than the fixed 160px carousel card.
const ACTION_MAX_LENGTH = {
  default: 130,
  homepage: 80,
  spending: 80
};

// Recents cards label each item with the collection it came from. Callers build
// that label as `collection_name || source || feed.name`: collection_name and
// feed.name are already human-readable, but `source` is the raw table and
// arrives as a slug ("executive_orders", "gao_reports"). That fallback is why
// most sources render capitalized and a handful don't. Normalizing here covers
// every caller (carousels, vertical lists, Glance) instead of each one fixing
// its own string.
const SOURCE_ACRONYMS = new Set([
  'us', 'usa', 'ai', 'api', 'pdf', 'rss', 'id',
  'gao', 'gpo', 'gsa', 'cbo', 'crs', 'omb', 'oig',
  'fbi', 'cia', 'nsa', 'dea', 'atf', 'ice', 'cbp', 'uscis', 'tsa', 'fema',
  'doj', 'dod', 'doe', 'dot', 'dhs', 'hhs', 'hud', 'usda', 'va',
  'sec', 'irs', 'fda', 'cdc', 'nih', 'nasa', 'nsf', 'noaa', 'ntsb', 'epa',
  'fcc', 'ftc', 'sba', 'ssa', 'osha', 'faa', 'fhwa', 'ferc', 'fdic', 'cftc',
  'nlrb', 'eeoc', 'cfpb', 'cms', 'bls', 'bea'
]);

// Kept lowercase when they fall inside a name ("Bureau of Labor Statistics").
const SOURCE_MINOR_WORDS = new Set(['a', 'an', 'and', 'as', 'at', 'by', 'for', 'in', 'of', 'on', 'or', 'the', 'to', 'vs']);

/**
 * FeedItem component displays a single feed item
 *
 * @param {Object} props
 * @param {Object} props.item - Feed item data
 * @param {string} props.feedName - Name of the feed
 * @param {string} props.branch - Branch name
 * @param {string} props.topic - Topic name
 * @param {string} props.buttonName - Label for the action button
 * @param {string} props.schema - Schema for routing
 * @param {string} props.table - Table for routing
 * @param {boolean} props.expanded - Whether to show the expanded view
 * @param {boolean} props.isRecentsItem - Whether this is a recents item (shows source header)
 * @param {string} props.variant - Style variant: "default" for FullFeedPage, "homepage" for carousels
 * @param {boolean} props.showViewMore - Whether to show the View More button (desktop only)
 * @param {string} props.viewMoreUrl - URL to navigate to when View More is clicked
 * @param {Array<React.ReactNode>} props.details - Optional extra lines under the date row.
 *   For items that do not come from a feed table -- api2's awards and stock
 *   trades on the Spending topic -- and so carry more than a title and a date.
 *   The card grows to fit them rather than clipping at the variant's height.
 * @param {string} props.shareUrl - Optional share link. Defaults to the item's
 *   /feed/{schema}/{table}/{id} page, which only exists for feed-table items.
 * @param {Function} props.onTitleClick - Optional title handler. Defaults to
 *   opening the document, else the source URL.
 * @param {boolean} props.showDate - Whether to show the date row. Items that
 *   put their date in a details line of their own ("first obligated Aug 31")
 *   turn it off rather than show the same date twice.
 * @param {string} props.moreUrl - Optional in-app detail page. When given, the
 *   first footer button is More and opens it, as it does for feed bills. For
 *   items whose detail page is not their own /feed/{schema}/{table}/{id}:
 *   api2's bills (by GovInfo package id) and House roll calls (by number and
 *   date) on the member page.
 *
 * The props below let an item that is not a document -- a government live
 * stream, say -- render as this same card rather than as a look-alike, so it
 * stays in step with every change made here. None of them changes a card that
 * doesn't pass them.
 *
 * @param {string} props.className - Extra classes on the outer .feed-item.
 * @param {Object} props.primaryAction - { label, icon, onClick }. Replaces
 *   the first footer button (Source / More) with a caller's own action, keeping
 *   its #billLink look. Streams use it for Watch.
 * @param {Function} props.onSourceClick - Handler for the recents source
 *   header. Defaults to opening /feed/{schema}/{table}; an item with no feed
 *   table passes its own, or the header does nothing.
 * @param {React.ReactNode} props.documentIcon - Icon for the document button,
 *   in place of the page icon. Streams show an external-link icon beside
 *   "YouTube".
 * @param {React.ReactNode} props.footerExtra - Rendered at the end of the
 *   footer, after Share. Streams on For You put their Hide button here.
 * @param {React.ReactNode} props.media - Rendered first inside the card, above
 *   the source header and title. Streams put their video thumbnail here.
 * @param {boolean} props.hideFooter - Leaves out the footer's buttons. For
 *   items whose actions all live somewhere else: a stream's Save, Share and
 *   YouTube are in its player, which the title opens.
 */
const FeedItem = ({
  item,
  feedName,
  branch,
  topic,
  buttonName = "Document",
  schema,
  table,
  expanded = false,
  isRecentsItem = false,
  variant = "default",
  showViewMore = false,
  viewMoreUrl = null,
  details = null,
  shareUrl = null,
  onTitleClick = null,
  showDate = true,
  moreUrl = null,
  className = '',
  primaryAction = null,
  onSourceClick = null,
  documentIcon = null,
  footerExtra = null,
  media = null,
  hideFooter = false
}) => {
  const { t } = useTranslation('common');

  // Share functionality states
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const navigate = useNavigate();

  // Use our collection hook for collection functionality
  const {
    isModalOpen,
    currentItem,
    saveSuccess,
    note,
    setNote,
    openCollectionModal,
    closeModal,
    handleSave,
    isStorageAvailable,
    CollectionButton
  } = useCollections();

  // Determine effective variant - spending tables get special treatment
  const effectiveVariant = (table === 'recent_awards' || table === 'doge_savings')
    ? 'spending'
    : variant;

  // Create share link. Items with no feed-table page of their own pass one in.
  const shareLink = shareUrl || `https://news.govglance.org/feed/${schema}/${table}/${item.id}`;

  // Format the title - only truncated to prevent absurdly long outliers
  const title = truncateText(item.status_title || item.title, TITLE_MAX_LENGTH);

  // Source label for the recents header - slug fallbacks get normalized
  const sourceLabel = formatSourceName(feedName);

  // Get document link
  const docLink = item.document_link || (item.download?.pdfLink) || null;

  // Crawl-backed items (Web Crawl Updates) show their page URL under the title.
  // normalizeCrawlItem sets isCrawlUpdate on those and on nothing else, so no
  // other feed is affected. The feed-item--crawl modifier gives the card room
  // for the extra line.
  const showCrawlUrl = !!(item.isCrawlUpdate && item.url);

  // Extra lines, when a caller supplies them. The feed-item--details modifier
  // lets the card grow to hold them.
  const detailLines = Array.isArray(details) ? details.filter(Boolean) : [];
  const hasDetails = detailLines.length > 0;

  // Where More goes: a caller's detail page, else a feed bill's own page.
  // Items with neither get Source instead.
  const moreTarget = moreUrl
    || (['congressional_bills', 'congressional_bills_enrolled', 'public_and_private_laws'].includes(table)
      ? `/feed/${schema}/${table}/${item.id}`
      : null);

  // Format the date for DISPLAY only
  const formattedDate = formatDate(item.created_at);

  // Handle clicking the source (for recents navigation). A caller's handler
  // wins; without one, only an item that has a feed table has somewhere to go.
  const handleSourceClick = (e) => {
    e.preventDefault();
    if (onSourceClick) {
      onSourceClick();
      return;
    }
    if (schema && table) navigate(`/feed/${schema}/${table}`);
  };

  // Handle clicking the title - a caller's handler if given, otherwise opens
  // the document link if available, otherwise the source url
  const handleTitleClick = (e) => {
    e.preventDefault();
    if (onTitleClick) {
      onTitleClick();
      return;
    }
    if (docLink) {
      openCapacitorSite(docLink, '_blank');
    } else {
      openCapacitorSite(item.url, '_blank');
    }
  };

  // Handle View More click
  const handleViewMoreClick = () => {
    if (viewMoreUrl) {
      navigate(viewMoreUrl);
    }
  };

  // Handle native sharing
  const handleNativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: title,
          url: shareLink,
        });
      } catch (error) {
        console.error('Error sharing:', error);
      }
    } else {
      alert(t('common:shareUnsupported', 'Sharing is not supported on this device.'));
    }
  };

  // Handle copy link to clipboard
  const handleCopyLink = () => {
    navigator.clipboard.writeText(shareLink)
      .then(() => setLinkCopied(true))
      .catch((error) => console.error('Error copying link:', error));
  };

  // Get most recent action if available
  const recentAction = item.actions ? getMostRecentActionDisplay(item.actions) : null;

  // The date / latest-action row. Full text is kept for the title attribute so
  // hovering still gives the whole string; only the rendered copy is cut.
  const metaFullText = (table === 'congressional_bills' && recentAction)
    ? `${formatDate(recentAction.action_date)} ${recentAction.action_description}`
    : formattedDate;

  const metaText = expanded
    ? metaFullText
    : truncateAtWord(metaFullText, ACTION_MAX_LENGTH[effectiveVariant] || ACTION_MAX_LENGTH.default);

  // Prepare the collection item data for our hook
  // IMPORTANT: Pass raw date (item.created_at), NOT the formatted date
  // The CollectionItem component will handle formatting for display
  const collectionItem = {
    title: title,
    url: item.url,
    docLink: docLink,
    date: item.created_at, // Raw date - CollectionItem handles formatting
    feedName: sourceLabel || feedName,
    branch: branch,
    topic: topic
  };

  return (
    <div className={`feed-item feed-item--${effectiveVariant} ${isRecentsItem ? 'feed-item--recents' : ''} ${showCrawlUrl ? 'feed-item--crawl' : ''} ${hasDetails ? 'feed-item--details' : ''} ${className} mb-4`}>
      <Card>
        {/* A caller's media (a stream's thumbnail) sits above everything else */}
        {media}

        {/* Clickable source header for recents - first element inside card body */}
        {isRecentsItem && sourceLabel && (
          <Card.Title
            id='billBodyTextSource'
            onClick={handleSourceClick}
            style={{ cursor: 'pointer' }}
          >
            {sourceLabel}
            <ChevronRight id="clickChevron" className="heading-chevron" />
          </Card.Title>
        )}

        <Card.Title
          id="billText"
          className="card-title p-3"
          onClick={handleTitleClick}
          style={{ cursor: 'pointer' }}
        >
          {title}
        </Card.Title>

        {/* Crawl updates: the page URL, directly under the title */}
        {showCrawlUrl && (
          <div
            className="feed-item-url"
            onClick={handleTitleClick}
            title={item.url}
          >
            {formatDisplayUrl(item.url)}
          </div>
        )}

        {showDate && (
          <Card.Body id="billBodyText" className="feed-item-meta" title={metaFullText}>
            {metaText}
          </Card.Body>
        )}

        {/* No #billBodyText here: rules written against that id size the
            feed's one-line date row, and they clip anything longer. */}
        {hasDetails && (
          <Card.Body className="feed-item-details">
            {detailLines.map((line, index) => (
              <div key={index} className="feed-item-detail">{line}</div>
            ))}
          </Card.Body>
        )}

        {(table === 'recent_awards' || table === 'doge_savings') && (
          <Card.Body id="billBodyText">
            {expanded ? item.description : truncateText(item.description, 40)}

            {item.award_amount && (
              <div className="mt-2">
                {t('common:totalValue', 'Total value:')} ${Number(item.award_amount || item.amount || 0).toLocaleString()}
              </div>
            )}

            {(item.saved_amount && item.saved_amount !== '0') && (
              <div className="mt-2">
                {t('common:savings', 'Savings:')} ${Number(item.saved_amount).toLocaleString()}
              </div>
            )}
          </Card.Body>
        )}

        {/* Row 1: Action buttons */}
        {!hideFooter && (
        <Card.Footer className="d-flex">
          {/* Source/More button, or the caller's own first action */}
          {primaryAction ? (
            <Button
              className="clickable me-2"
              id="billLink"
              onClick={primaryAction.onClick}
            >
              {primaryAction.icon} {primaryAction.label}
            </Button>
          ) : moreTarget ? (
            <Button
              className="clickable me-2"
              id="billLink"
              onClick={() => navigate(moreTarget)}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-info-icon lucide-info"><circle cx="12" cy="12" r="10"/><path d="M12 16v-4"/><path d="M12 8h.01"/></svg> {t('common:more', 'More')}
            </Button>
          ) : (
            <Button
              className="clickable me-2"
              id="billLink"
              onClick={() => openCapacitorSite(item.url, "_blank")}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-info-icon lucide-info"><circle cx="12" cy="12" r="10"/><path d="M12 16v-4"/><path d="M12 8h.01"/></svg> {t('common:source', 'Source')}
            </Button>
          )}

          {/* Document button */}
          {docLink && buttonName && (
            <Button
              className="clickable me-2"
              id="viewBillButton"
              onClick={() => openCapacitorSite(docLink, "_blank")}
            >
              {documentIcon || <FileText />} {buttonName === 'Document' ? t('common:document', 'Document') : buttonName}
            </Button>
          )}

          {/* Use our collection button from the hook */}
          <CollectionButton
            item={collectionItem}
            className="me-2"
          />

          {/* Share button */}
          <Button
            className="clickable"
            id="shareButton"
            onClick={() => setShareModalOpen(true)}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-send-horizontal-icon lucide-send-horizontal"><path d="M3.714 3.048a.498.498 0 0 0-.683.627l2.843 7.627a2 2 0 0 1 0 1.396l-2.842 7.627a.498.498 0 0 0 .682.627l18-8.5a.5.5 0 0 0 0-.904z"/><path d="M6 12h16"/></svg>
          </Button>

          {footerExtra}
        </Card.Footer>
        )}

        {/* Row 2: View More button - only shown on desktop via showViewMore prop */}
        {showViewMore && viewMoreUrl && (
          <Button
            className="clickable"
            id="whDocsViewMoreButton"
            onClick={handleViewMoreClick}
          >
            <Files size={20} /> {t('common:viewMore', 'View More')}
          </Button>
        )}
      </Card>

      {/* Share Modal */}
      <Modal id="fullBillModal" show={shareModalOpen} size="xl" centered onHide={() => setShareModalOpen(false)}>
        <Modal.Header id="billModalHeader" closeButton>
          <h3>{t('common:share', 'Share')}</h3>
        </Modal.Header>
        <Modal.Body id="descriptionModalBody">
          <Form.Group id="noteModalForm">
            <Form.Control id="noteModalFeedItem" type="text" readOnly value={shareLink} />
          </Form.Group>
          <Modal.Footer id="shareFooter">
            <Button
              className="clickable me-2"
              id="addToCollectionButton"
              onClick={handleNativeShare}
            >
              <BoxArrowUp />
            </Button>
            <Button
              className="clickable"
              id="addToCollectionButton"
              onClick={handleCopyLink}
            >
              {linkCopied ? <ClipboardCheck /> : <Clipboard />}
            </Button>
          </Modal.Footer>
        </Modal.Body>
      </Modal>

      {/* Use the CollectionModal component */}
      <CollectionModal
        isModalOpen={isModalOpen}
        currentItem={currentItem}
        saveSuccess={saveSuccess}
        note={note}
        setNote={setNote}
        handleSave={handleSave}
        closeModal={closeModal}
        isStorageAvailable={isStorageAvailable}
      />
    </div>
  );
};

// Helper functions
function truncateText(text, maxLength = 180) {
  if (!text) return '';
  return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
}

// Same as truncateText but backs up to the last space so the cut lands between
// words rather than mid-word. Falls back to a hard cut if there's no space in
// range (single long token).
function truncateAtWord(text, maxLength) {
  if (!text) return '';
  const value = String(text);
  if (value.length <= maxLength) return value;

  const slice = value.substring(0, maxLength);
  const lastSpace = slice.lastIndexOf(' ');
  const cut = lastSpace > maxLength * 0.6 ? slice.substring(0, lastSpace) : slice;

  // Drop trailing punctuation so we don't end up with ",..." or " -...".
  return cut.replace(/[\s,;:.\-]+$/, '') + '...';
}

// Turn a collection/table identifier into a readable source label:
// "executive_orders" -> "Executive Orders", "gao_reports" -> "GAO Reports".
// Names that already contain a space and some capitalization are assumed to be
// curated display names and are returned untouched, so this only cleans up the
// raw `source` fallback.
function formatSourceName(name) {
  if (!name) return '';
  const value = String(name).trim();
  if (!value) return '';
  if (/\s/.test(value) && /[A-Z]/.test(value)) return value;

  return value
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .split(' ')
    .map((word, index) => {
      const lower = word.toLowerCase();
      if (SOURCE_ACRONYMS.has(lower)) return lower.toUpperCase();
      if (index > 0 && SOURCE_MINOR_WORDS.has(lower)) return lower;
      return lower.charAt(0).toUpperCase() + lower.slice(1);
    })
    .join(' ');
}

// Trim a URL for display: drop the scheme, the www. prefix and any trailing
// slash, then truncate the path while keeping the host intact so the source is
// always readable.
function formatDisplayUrl(url, maxLength = URL_MAX_LENGTH) {
  if (!url) return '';
  const display = String(url)
    .replace(/^https?:\/\//i, '')
    .replace(/^www\./i, '')
    .replace(/\/+$/, '');

  if (display.length <= maxLength) return display;

  const slashIndex = display.indexOf('/');
  if (slashIndex === -1) return display.substring(0, maxLength) + '...';

  const host = display.substring(0, slashIndex);
  const path = display.substring(slashIndex);
  const room = maxLength - host.length - 3;

  // Host alone is already at or over the budget - truncate the whole string.
  if (room <= 4) return display.substring(0, maxLength) + '...';

  return host + path.substring(0, room) + '...';
}

function formatDate(inputDate) {
  if (!inputDate) return '';

  // If it's already in M-D-YYYY format, return as-is
  if (typeof inputDate === 'string' && /^\d{1,2}-\d{1,2}-\d{4}$/.test(inputDate)) {
    return inputDate;
  }

  try {
    const date = new Date(inputDate);
    if (isNaN(date.getTime())) return '';

    const utcDate = new Date(date.getTime() + date.getTimezoneOffset() * 60000);
    const day = utcDate.getDate();
    const month = utcDate.getMonth() + 1;
    const year = utcDate.getFullYear();
    return `${month}-${day}-${year}`;
  } catch (error) {
    return '';
  }
}

function getMostRecentActionDisplay(actions) {
  if (!actions || actions.length === 0) return null;
  return actions.sort((a, b) => new Date(b.action_date) - new Date(a.action_date))[0];
}

export default FeedItem;